import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, Award } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';

// Example fallback toast hook if 'use-toast' is not found or needs a fallback
const useToastFallback = () => ({
  toast: () => { }, // Placeholder for toast functionality
});

// Use the actual hook or the fallback
const useAppToast = () => {
  try {
    // Attempt to import and use the actual hook
    return useToast();
  } catch (error) {
    // If the import fails, use the fallback
    console.warn("useToast hook not found, using fallback.");
    return useToastFallback();
  }
};


export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  // Destructure toast from the hook
  const { toast } = useAppToast();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Always use the server-side PHP handler (works in dev via npm proxy config and on production)
      const response = await fetch('contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Message Sent Successfully",
          description: "Thank you for your message! We'll get back to you within 24 hours.",
          variant: "success",
        });
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        toast({
          title: "Error Sending Message",
          description: result.message || "Sorry, there was an error sending your message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Network Error",
        description: "Sorry, there was a network error. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const services = [
    'Celebrity Management',
    'Event Management',
    'Sponsorship Management',
    'Merchandising & Corporate Gifting',
    'Sporting Experiences'
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-slate-800 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-gray-800 to-gray-900"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-amber-400/10 to-orange-500/10 rounded-full blur-3xl"></div>

        {/* Subtle Gradient Orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-amber-400/10 to-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-purple-400/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-white">
            Let's Create Something
            <span className="block bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              Extraordinary
            </span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to bring your vision to life? Whether it's securing the perfect celebrity ambassador, planning an unforgettable sporting event, or creating experiences that wow your audience - we're here to make it happen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-white/95 backdrop-blur-md border border-gray-200 shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">Send us a Message</CardTitle>
                <CardDescription className="text-gray-600">
                  Fill out the form below and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-900">Name</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        required
                        className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-blue-400"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-900">Email</label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                        className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-blue-400"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900">Phone (Optional)</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-blue-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900">Service Interest</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:border-blue-400 focus:outline-none"
                    >
                      <option value="" className="text-gray-900">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service} className="text-gray-900">
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-900">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project and requirements..."
                      rows={5}
                      required
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-blue-400 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl py-3 text-lg group"
                  >
                    Send Message
                    <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 icon-outline" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <Card className="bg-white/95 backdrop-blur-md border border-gray-200 shadow-2xl">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">Get in Touch</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white icon-outline" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Email Us</p>
                    <p className="text-gray-600">connect@centrestageent.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white icon-outline" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Call Us</p>
                    <p className="text-gray-600">+91 98201 72766</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white icon-outline" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Visit Us</p>
                    <a
                      href="https://maps.app.goo.gl/vsbNhYiXP5B87QmS8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-500 transition-colors duration-300 underline"
                    >
                      Mumbai, India
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};